import {
  NewOccupantRequestSchema,
  OccupancyProofContentType,
  type EnrollmentRejectionReason,
  type NewOccupantRequest,
  type OccupancyProofContentType as OccupancyProofContentTypeT,
  type OccupancyProofDocument,
} from '@rheora/enrollment-contract';

/**
 * Switch-hold self-serve resolution form logic (E-SF07 AC1) — kept OUT of the
 * `.tsx` per CLAUDE.md (components render; logic lives here). Validation reuses
 * the shared contract Zod schema so the FE and BFF agree on a well-formed
 * submission. Follows the E-SF02..E-SF06 `useState` + service-Zod precedent
 * (`@fluxora/forms` is a non-functional stub in US-Shop; see credit.service.ts).
 */

/** Max proof file size accepted client-side (~ the contract's 40 KB base64 cap). */
export const MAX_PROOF_BYTES = 28_000;

/** The document types the proof upload accepts (matches the contract allowlist). */
export const ACCEPTED_PROOF_TYPES: readonly OccupancyProofContentTypeT[] = [
  'application/pdf',
  'image/jpeg',
  'image/png',
];

export interface NewOccupantForm {
  /** The prior occupant, if known — helps ops match the hold. Optional. */
  readonly priorOccupantName: string;
  /** YYYY-MM-DD move-in date (native date input value). */
  readonly moveInDate: string;
  readonly occupancyType: 'OWN' | 'RENT' | 'OTHER' | '';
  readonly acknowledged: boolean;
  /** The read + validated proof document, set after a successful file read. */
  readonly proof: OccupancyProofDocument | null;
  /** The chosen file's name, for display before/after read. */
  readonly proofFilename: string;
}

export const emptyNewOccupantForm: NewOccupantForm = {
  priorOccupantName: '',
  moveInDate: '',
  occupancyType: '',
  acknowledged: false,
  proof: null,
  proofFilename: '',
};

export type NewOccupantFormErrors = Partial<Record<keyof NewOccupantForm, string>>;

export interface NewOccupantValidation {
  readonly ok: boolean;
  readonly errors: NewOccupantFormErrors;
  readonly value: NewOccupantRequest | undefined;
}

export interface ProofReadResult {
  readonly ok: boolean;
  /** i18n key when the file was rejected (too big / wrong type / read failed). */
  readonly error?: string;
  readonly proof?: OccupancyProofDocument;
}

/**
 * Read a chosen proof file to a validated `OccupancyProofDocument` (metadata +
 * base64). Rejects an over-size or wrong-type file client-side (the server
 * re-validates). Uses `FileReader` (available in the browser + jsdom).
 */
export async function readProofFile(file: File): Promise<ProofReadResult> {
  if (!ACCEPTED_PROOF_TYPES.includes(file.type as OccupancyProofContentTypeT)) {
    return { ok: false, error: 'shop.status.resolve.proof.type' };
  }
  if (file.size > MAX_PROOF_BYTES) {
    return { ok: false, error: 'shop.status.resolve.proof.size' };
  }

  const dataUrl = await new Promise<string | null>((resolve) => {
    const reader = new FileReader();
    reader.onerror = (): void => resolve(null);
    reader.onload = (): void =>
      resolve(typeof reader.result === 'string' ? reader.result : null);
    reader.readAsDataURL(file);
  });

  if (dataUrl === null) return { ok: false, error: 'shop.status.resolve.proof.read' };
  // Strip the `data:<mime>;base64,` prefix — the contract wants raw base64.
  const comma = dataUrl.indexOf(',');
  const base64 = comma >= 0 ? dataUrl.slice(comma + 1) : '';
  const contentType = OccupancyProofContentType.safeParse(file.type);
  if (!contentType.success || base64.length === 0) {
    return { ok: false, error: 'shop.status.resolve.proof.read' };
  }

  const proof: OccupancyProofDocument = {
    filename: file.name,
    contentType: contentType.data,
    contentBase64: base64,
  };
  return { ok: true, proof };
}

/**
 * Validate the resolution form. Field errors are i18n KEYS (resolved by the
 * component). The shared contract schema is the final arbiter.
 */
export function validateNewOccupantForm(form: NewOccupantForm): NewOccupantValidation {
  const errors: NewOccupantFormErrors = {};

  if (!/^\d{4}-\d{2}-\d{2}$/u.test(form.moveInDate)) {
    errors.moveInDate = 'shop.status.resolve.moveInDate.invalid';
  }
  if (form.occupancyType === '') {
    errors.occupancyType = 'shop.status.resolve.occupancyType.required';
  }
  if (!form.acknowledged) {
    errors.acknowledged = 'shop.status.resolve.acknowledged.required';
  }
  if (form.proof === null) {
    errors.proof = 'shop.status.resolve.proof.required';
  }

  if (Object.keys(errors).length > 0) return { ok: false, errors, value: undefined };

  const priorName = form.priorOccupantName.trim();
  const parsed = NewOccupantRequestSchema.safeParse({
    statement: {
      ...(priorName.length > 0 ? { priorOccupantName: priorName } : {}),
      moveInDate: form.moveInDate,
      occupancyType: form.occupancyType,
      acknowledged: true,
    },
    proof: form.proof,
  });
  if (!parsed.success) {
    return { ok: false, errors: { proof: 'shop.status.resolve.proof.required' }, value: undefined };
  }
  return { ok: true, errors: {}, value: parsed.data };
}

/**
 * The i18n key(s) for a rejection's "what to do next" copy. A switch-hold routes
 * the customer to the self-serve resolution form; everything else routes to
 * support (the copy the keys resolve to).
 */
export function nextStepsKeyFor(reason: EnrollmentRejectionReason): string {
  switch (reason) {
    case 'SWITCH_HOLD':
      return 'shop.status.rejected.switchHold.nextSteps';
    case 'RESCINDED':
      return 'shop.status.rejected.rescinded.nextSteps';
    case 'CANCELLED':
      return 'shop.status.rejected.cancelled.nextSteps';
    default:
      return 'shop.status.rejected.other.nextSteps';
  }
}
